import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { formatUnits } from 'viem';
import { Trophy, TrendingUp, Award, Gift, CheckCircle, Link2, ChevronDown, ChevronUp, Sparkles, Clock, Timer, Users, Coins, History, CalendarDays } from 'lucide-react';
import { formatNumber, formatAddress } from '../lib/utils';
import { TreasuryBalance } from '../components/TreasuryBalance';
import { ClaimCard } from '../components/ClaimCard';
import { WalletConnectSection } from '../components/WalletConnectButton';
import { MyQRAccess } from '../components/MyQRAccess';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_LEADERBOARD_DISPLAY = 50;

// Scoring weight constants (must match backend scoring_engine.py)
const TIPPER_WEIGHT = 0.75;
const CREATOR_WEIGHT = 0.25;

// Format BNB from wei string (PURE STRING - no float conversion)
// Uses formatUnits which returns decimal string, then string manipulation for 6 decimals
const formatBnbAmount = (weiString, fallbackString) => {
  let decimalStr;
  
  if (weiString) {
    // formatUnits returns string like "0.038950000000000000"
    decimalStr = formatUnits(BigInt(weiString), 18);
  } else if (fallbackString) {
    decimalStr = fallbackString;
  } else {
    return '0.000000';
  }
  
  // Pure string manipulation to get exactly 6 decimal places
  const parts = decimalStr.split('.');
  const intPart = parts[0] || '0';
  let decPart = parts[1] || '';
  
  // Pad or trim to exactly 6 decimals
  if (decPart.length < 6) {
    decPart = decPart.padEnd(6, '0');
  } else if (decPart.length > 6) {
    decPart = decPart.slice(0, 6);
  }
  
  return `${intPart}.${decPart}`;
};

// Countdown Timer Component
const EpochCountdown = ({ endTime }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const target = new Date(endTime).getTime();
      const now = new Date().getTime();
      const difference = target - now;

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime]);

  if (isExpired) {
    return (
      <div className="flex items-center gap-2 text-amber-600">
        <Clock className="w-4 h-4" />
        <span className="text-sm font-medium">Epoch ending soon...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <Timer className="w-4 h-4 text-[#f0b90b]" />
      <div className="flex items-center gap-1 text-sm">
        <span className="font-bold text-[#1a1a1a]">{timeLeft.days}d</span>
        <span className="text-[#666]">:</span>
        <span className="font-bold text-[#1a1a1a]">{String(timeLeft.hours).padStart(2, '0')}h</span>
        <span className="text-[#666]">:</span>
        <span className="font-bold text-[#1a1a1a]">{String(timeLeft.minutes).padStart(2, '0')}m</span>
        <span className="text-[#666]">:</span>
        <span className="font-bold text-[#1a1a1a]">{String(timeLeft.seconds).padStart(2, '0')}s</span>
      </div>
    </div>
  );
};

export const RewardsPage = () => {
  const { address, isConnected } = useAccount();
  const [allEpochs, setAllEpochs] = useState([]);
  const [selectedEpochId, setSelectedEpochId] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRewards, setUserRewards] = useState(null);
  const [userEpochData, setUserEpochData] = useState([]);
  const [category, setCategory] = useState('overall');
  const [loading, setLoading] = useState(true);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showClaimHistory, setShowClaimHistory] = useState(false);
  
  // Initial load - fetch all epochs
  useEffect(() => {
    loadEpochs();
  }, []);
  
  // Load leaderboard when epoch or category changes
  useEffect(() => {
    if (selectedEpochId) {
      loadLeaderboard(selectedEpochId);
    }
  }, [selectedEpochId, category]);
  
  // Load user rewards when connected
  useEffect(() => {
    if (isConnected && address) {
      loadUserRewards();
    }
  }, [address, isConnected]);
  
  const loadEpochs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/epochs`);
      const epochs = res.data.epochs || [];
      setAllEpochs(epochs);
      
      // Select the most recent epoch by default
      if (epochs.length > 0) {
        setSelectedEpochId(epochs[0].epochId);
      }
    } catch (error) {
      console.error('Error loading epochs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadLeaderboard = async (epochId) => {
    try {
      setLeaderboardLoading(true);
      const res = await axios.get(
        `${BACKEND_URL}/api/leaderboard/epoch/${epochId}?category=${category}`
      );
      setLeaderboard(res.data.leaderboard || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      setLeaderboard([]);
    } finally {
      setLeaderboardLoading(false);
    }
  };
  
  const loadUserRewards = async () => {
    try {
      const rewardsRes = await axios.get(`${BACKEND_URL}/api/rewards/${address}`);
      setUserRewards(rewardsRes.data);
      
      const epochIds = [...new Set(rewardsRes.data.epochs?.map(e => e.epochId) || [])];
      const epochPromises = epochIds.map(id => 
        axios.get(`${BACKEND_URL}/api/epochs/${id}`).catch(() => null)
      );
      const epochResponses = await Promise.all(epochPromises);
      setUserEpochData(epochResponses.filter(r => r !== null).map(r => r.data));
    } catch (error) {
      console.error('Error loading user rewards:', error);
    }
  };
  
  const selectedEpoch = allEpochs.find(e => e.epochId === selectedEpochId);
  const isActiveEpoch = selectedEpoch?.status === 'COLLECTING' || selectedEpoch?.status === 'ACTIVE';
  
  // Find user's rank in current leaderboard
  const userRank = leaderboard.findIndex(e => e.wallet?.toLowerCase() === address?.toLowerCase()) + 1;
  const userEntry = leaderboard.find(e => e.wallet?.toLowerCase() === address?.toLowerCase());
  
  const unclaimedRewards = userRewards?.epochs?.filter(e => !e.claimed) || [];
  const claimedRewards = userRewards?.epochs?.filter(e => e.claimed) || [];
  
  // Get status badge styling
  const getStatusBadge = (status) => {
    switch(status) {
      case 'COLLECTING':
        return { bg: 'bg-amber-500', text: 'PROVISIONAL', icon: Sparkles };
      case 'ACTIVE':
        return { bg: 'bg-green-500', text: 'LIVE', icon: Sparkles };
      case 'CLAIMABLE':
        return { bg: 'bg-[#f0b90b]', text: 'CLAIMABLE', icon: Gift };
      case 'FINALIZED':
        return { bg: 'bg-blue-500', text: 'COMPUTED', icon: CheckCircle };
      default:
        return { bg: 'bg-gray-500', text: status, icon: CheckCircle };
    }
  };
  
  return (
    <div className="min-h-screen py-12 bg-[#ccc8bc]" data-testid="rewards-page">
      <div className="container mx-auto px-4 max-w-6xl">
        
        {/* Wallet Connect */}
        <WalletConnectSection 
          message="Connect your wallet to view and claim your rewards" 
          className="mb-10"
        />
        
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-white/80 rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-[#f0b90b] rounded-full"></span>
            <span className="text-sm font-medium text-[#1a1a1a]">REWARDS</span>
          </div>
          <h1 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 300 }} className="text-4xl md:text-5xl mb-4 text-[#1a1a1a]">
            TipMe Rewards
          </h1>
          <p className="text-[#666] max-w-xl mx-auto mb-6">
            Earn rewards for tipping and creating. A portion of each transaction fuels the ecosystem.
          </p>
          
          {/* Friendly Explanation */}
          <div className="max-w-2xl mx-auto bg-white/60 rounded-2xl p-5 text-left">
            <p className="text-[#555] text-sm leading-relaxed">
              <strong className="text-[#1a1a1a]">How it works:</strong> Every week, we distribute BNB rewards to active participants. 
              Anyone can tip using our community token <strong className="text-[#f0b90b]">$TIP</strong> and earn TRUE Points — only creators need to verify their wallet to receive tips. 
              At the end of each epoch, rewards are calculated based on your points and you can claim your share directly to your wallet. 
              Tippers earn {Math.round(TIPPER_WEIGHT * 100)}% of the reward pool, creators earn {Math.round(CREATOR_WEIGHT * 100)}%. 
              <span className="text-[#f0b90b] font-medium"> Happy tipping! 🎉</span>
            </p>
          </div>
        </div>
        
        {/* Treasury Balance - Compact pill */}
        <div className="flex justify-center mb-10">
          <TreasuryBalance tokenAddress={process.env.REACT_APP_TIP_TOKEN_ADDRESS} />
        </div>
        
        {/* Claim Section — Latest epoch + collapsible history */}
        {isConnected && unclaimedRewards.length > 0 && (() => {
          // Sort by epochId descending, latest first
          const sorted = [...unclaimedRewards].sort((a, b) => b.epochId - a.epochId);
          const latestReward = sorted[0];
          const previousRewards = sorted.slice(1);
          const latestEpoch = userEpochData.find(e => e.epochId === latestReward.epochId);
          
          return (
            <div className="mb-10">
              <div className="bg-[#fffbeb] border border-[#f0b90b]/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#f0b90b] rounded-full flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#1a1a1a]" data-testid="claim-rewards-title">Claim Your Rewards</h2>
                  </div>
                </div>
                
                {/* Latest epoch — always visible as full card */}
                {latestEpoch && (
                  <ClaimCard 
                    key={latestReward.epochId} 
                    epoch={latestEpoch} 
                    reward={latestReward}
                    wallet={address}
                    onClaimSuccess={loadUserRewards}
                  />
                )}
                
                {/* Info text */}
                <p className="text-xs text-[#999] mt-3 text-center">
                  Unclaimed rewards roll into the next epoch after 5 days.
                </p>
                
                {/* Previous epochs — collapsible */}
                {previousRewards.length > 0 && (
                  <div className="mt-3">
                    <button
                      onClick={() => setShowClaimHistory(!showClaimHistory)}
                      className="flex items-center gap-2 text-sm text-[#888] hover:text-[#555] transition-colors py-1.5 px-1"
                      data-testid="previous-epochs-btn"
                    >
                      <History className="w-3.5 h-3.5" />
                      <span>Previous epochs ({previousRewards.length})</span>
                      {showClaimHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                    
                    {showClaimHistory && (
                      <div className="mt-2 grid gap-2" data-testid="previous-epochs-list">
                        {previousRewards.map(reward => {
                          const epoch = userEpochData.find(e => e.epochId === reward.epochId);
                          return epoch ? (
                            <ClaimCard 
                              key={reward.epochId} 
                              epoch={epoch} 
                              reward={reward}
                              wallet={address}
                              onClaimSuccess={loadUserRewards}
                            />
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        
        {/* Claimed History */}
        {isConnected && claimedRewards.length > 0 && (
          <div className="mb-10">
            <details className="bg-white/50 rounded-2xl border border-[#e0dbd3]">
              <summary className="cursor-pointer p-4 flex items-center gap-3 font-medium text-[#333]">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Claimed Rewards ({claimedRewards.length})</span>
              </summary>
              <div className="p-4 pt-0 grid gap-3">
                {claimedRewards.map(reward => {
                  const epoch = userEpochData.find(e => e.epochId === reward.epochId);
                  return epoch ? (
                    <ClaimCard 
                      key={reward.epochId} 
                      epoch={epoch} 
                      reward={reward}
                      wallet={address}
                      onClaimSuccess={loadUserRewards}
                    />
                  ) : null;
                })}
              </div>
            </details>
          </div>
        )}
        
        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          
          {/* Leaderboard */}
          <div className="bg-[#ebe6de] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-[#1a1a1a]" data-testid="leaderboard-title">Leaderboard</h2>
            </div>
            
            {/* Epoch Summary Card */}
            {selectedEpoch && (() => {
              const badge = getStatusBadge(selectedEpoch.status);
              const BadgeIcon = badge.icon;
              const startDate = selectedEpoch.startTime ? new Date(selectedEpoch.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
              const endDate = selectedEpoch.endTime ? new Date(selectedEpoch.endTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
              const pastEpochs = allEpochs.filter(e => e.epochId !== selectedEpochId);
              
              return (
                <div className="mb-4" data-testid="epoch-summary-card">
                  {/* Active Summary */}
                  <div className="bg-white rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2.5">
                        <span className="text-lg font-bold text-[#1a1a1a]">Epoch {selectedEpoch.epochId}</span>
                        <div className={`flex items-center gap-1 ${badge.bg} text-white text-xs font-medium px-2.5 py-1 rounded-full`}>
                          <BadgeIcon className="w-3 h-3" />
                          <span>{badge.text}</span>
                        </div>
                      </div>
                      {isActiveEpoch && selectedEpoch.endTime && (
                        <EpochCountdown endTime={selectedEpoch.endTime} />
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-[#888]">
                      <span className="flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {startDate}{endDate ? ` – ${endDate}` : ''}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        {selectedEpoch.totalParticipants || 0} participants
                      </span>
                      {selectedEpoch.totalRewardPool && selectedEpoch.totalRewardPool !== '0' && (
                        <span className="flex items-center gap-1.5">
                          <Coins className="w-3.5 h-3.5" />
                          {formatBnbAmount(selectedEpoch.totalRewardPool)} BNB
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* View History Toggle */}
                  {pastEpochs.length > 0 && (
                    <div className="mt-2">
                      <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="flex items-center gap-2 text-sm text-[#888] hover:text-[#555] transition-colors py-1.5 px-1"
                        data-testid="view-history-btn"
                      >
                        <History className="w-3.5 h-3.5" />
                        <span>View History ({pastEpochs.length})</span>
                        {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      
                      {showHistory && (
                        <div className="mt-1 space-y-1 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="epoch-history-list">
                          {pastEpochs.map((epoch) => {
                            const pastBadge = getStatusBadge(epoch.status);
                            const pStart = epoch.startTime ? new Date(epoch.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                            const pEnd = epoch.endTime ? new Date(epoch.endTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                            return (
                              <button
                                key={epoch.epochId}
                                onClick={() => { setSelectedEpochId(epoch.epochId); setShowHistory(false); }}
                                className="w-full flex items-center justify-between bg-white/60 hover:bg-white rounded-lg px-3.5 py-2.5 text-left transition-colors group"
                                data-testid={`epoch-history-item-${epoch.epochId}`}
                              >
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-semibold text-[#333] group-hover:text-[#1a1a1a]">Epoch {epoch.epochId}</span>
                                  <span className="text-xs text-[#aaa]">{pStart}{pEnd ? ` – ${pEnd}` : ''}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-[#aaa]">{epoch.totalParticipants || 0} participants</span>
                                  <div className={`w-2 h-2 rounded-full ${pastBadge.bg}`}></div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Category Tabs */}
            <div className="flex gap-1 p-1 bg-[#d4cfc4] rounded-xl mb-4" data-testid="leaderboard-tabs">
              {['overall', 'tippers', 'creators', 'scouts'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  data-testid={`tab-${cat}`}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all capitalize ${
                    category === cat
                      ? 'bg-white text-[#1a1a1a] shadow-sm'
                      : 'text-[#666] hover:text-[#333]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            
            {/* Leaderboard List - Scrollable */}
            <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1 scrollbar-thin scrollbar-thumb-[#d4cfc4] scrollbar-track-transparent">
              {leaderboardLoading ? (
                <div className="text-center py-8 text-[#888]">
                  <div className="animate-spin w-6 h-6 border-2 border-[#f0b90b] border-t-transparent rounded-full mx-auto mb-2"></div>
                  Loading leaderboard...
                </div>
              ) : (
                <>
                  {leaderboard.slice(0, MAX_LEADERBOARD_DISPLAY).map((entry, index) => {
                    const isCurrentUser = entry.wallet?.toLowerCase() === address?.toLowerCase();
                    return (
                      <div
                        key={entry.wallet}
                        className={`rounded-xl p-3 flex items-center gap-3 transition-all ${
                          isCurrentUser 
                            ? 'bg-[#f0b90b]/20 border-2 border-[#f0b90b]/50' 
                            : 'bg-white/70 hover:bg-white/90'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                          index === 0 ? 'bg-[#f0b90b] text-white' :
                          index === 1 ? 'bg-[#c0c0c0] text-white' :
                          index === 2 ? 'bg-[#cd7f32] text-white' :
                          'bg-[#e0dbd3] text-[#666]'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-sm text-[#333] truncate flex items-center gap-2">
                            {formatAddress(entry.wallet)}
                            {isCurrentUser && <span className="text-xs bg-[#f0b90b] text-white px-2 py-0.5 rounded-full">You</span>}
                          </div>
                          {/* Role Badges */}
                          <div className="flex items-center gap-1 mt-0.5 mb-0.5">
                            {parseFloat(entry.creatorPoints || 0) > 0 && (
                              <span className="text-[10px] font-medium text-[#8b6914] bg-[#f0b90b]/15 px-1.5 py-[1px] rounded" data-testid="badge-creator">Creator</span>
                            )}
                            {parseFloat(entry.tipperPoints || 0) > 0 && (
                              <span className="text-[10px] font-medium text-[#5a6d4e] bg-[#6b8e5b]/15 px-1.5 py-[1px] rounded" data-testid="badge-tipper">Tipper</span>
                            )}
                            {parseFloat(entry.scoutPoints || 0) > 0 && (
                              <span className="text-[10px] font-medium text-[#6b5b8a] bg-[#8b6bb5]/15 px-1.5 py-[1px] rounded" data-testid="badge-scout">Scout</span>
                            )}
                          </div>
                          {/* Component Breakdown */}
                          <div className="text-xs text-[#888]">
                            {[
                              parseFloat(entry.creatorPoints || 0) > 0 ? `Creator ${formatNumber(parseFloat(entry.creatorPoints))}` : null,
                              parseFloat(entry.tipperPoints || 0) > 0 ? `Tipper ${formatNumber(parseFloat(entry.tipperPoints))}` : null,
                              parseFloat(entry.scoutPoints || 0) > 0 ? `Scout ${formatNumber(parseFloat(entry.scoutPoints))}` : null,
                            ].filter(Boolean).join(' · ') || 'No points yet'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-[#1a1a1a]">
                            {formatNumber(
                              category === 'creators' ? parseFloat(entry.creatorPoints || 0) :
                              category === 'tippers' ? parseFloat(entry.tipperPoints || 0) :
                              category === 'scouts' ? parseFloat(entry.scoutPoints || 0) :
                              parseFloat(entry.totalTruePoints || 0)
                            )}
                          </div>
                          <div className="text-xs text-[#888]">TRUE pts</div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Show user's rank if outside top displayed */}
                  {isConnected && userRank > MAX_LEADERBOARD_DISPLAY && userEntry && (
                    <div className="mt-4 pt-4 border-t border-[#d4cfc4]">
                      <p className="text-xs text-[#888] mb-2">Your Position</p>
                      <div className="bg-[#f0b90b]/20 border-2 border-[#f0b90b]/50 rounded-xl p-3 flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-[#e0dbd3] flex items-center justify-center text-sm font-bold text-[#666]">
                          {userRank}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-sm text-[#333] truncate flex items-center gap-2">
                            {formatAddress(address)}
                            <span className="text-xs bg-[#f0b90b] text-white px-2 py-0.5 rounded-full">You</span>
                          </div>
                          {/* Role Badges */}
                          <div className="flex items-center gap-1 mt-0.5 mb-0.5">
                            {parseFloat(userEntry.creatorPoints || 0) > 0 && (
                              <span className="text-[10px] font-medium text-[#8b6914] bg-[#f0b90b]/15 px-1.5 py-[1px] rounded">Creator</span>
                            )}
                            {parseFloat(userEntry.tipperPoints || 0) > 0 && (
                              <span className="text-[10px] font-medium text-[#5a6d4e] bg-[#6b8e5b]/15 px-1.5 py-[1px] rounded">Tipper</span>
                            )}
                            {parseFloat(userEntry.scoutPoints || 0) > 0 && (
                              <span className="text-[10px] font-medium text-[#6b5b8a] bg-[#8b6bb5]/15 px-1.5 py-[1px] rounded">Scout</span>
                            )}
                          </div>
                          {/* Component Breakdown */}
                          <div className="text-xs text-[#888]">
                            {[
                              parseFloat(userEntry.creatorPoints || 0) > 0 ? `Creator ${formatNumber(parseFloat(userEntry.creatorPoints))}` : null,
                              parseFloat(userEntry.tipperPoints || 0) > 0 ? `Tipper ${formatNumber(parseFloat(userEntry.tipperPoints))}` : null,
                              parseFloat(userEntry.scoutPoints || 0) > 0 ? `Scout ${formatNumber(parseFloat(userEntry.scoutPoints))}` : null,
                            ].filter(Boolean).join(' · ') || 'No points yet'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-[#1a1a1a]">
                            {formatNumber(
                              category === 'creators' ? parseFloat(userEntry.creatorPoints || 0) :
                              category === 'tippers' ? parseFloat(userEntry.tipperPoints || 0) :
                              category === 'scouts' ? parseFloat(userEntry.scoutPoints || 0) :
                              parseFloat(userEntry.totalTruePoints || 0)
                            )}
                          </div>
                          <div className="text-xs text-[#888]">TRUE pts</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {leaderboard.length === 0 && (
                    <div className="text-center py-8 text-[#888]">
                      No tips recorded yet for this epoch.
                    </div>
                  )}
                  
                  {leaderboard.length > MAX_LEADERBOARD_DISPLAY && (
                    <div className="text-center py-2 text-xs text-[#888]">
                      Showing top {MAX_LEADERBOARD_DISPLAY} of {leaderboard.length} participants
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Finalization Notice */}
            <div className="mt-4 pt-3 border-t border-[#d4cfc4] text-xs text-[#666]">
              TRUE Points are provisional during the epoch. Final scores are calculated after epoch close and may take up to 12 hours to appear.{' '}
              <a 
                href="https://tipme.space/how-it-works" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#f0b90b] hover:underline"
              >
                How rewards work
              </a>
            </div>
          </div>
          
          {/* T.R.U.E. Model */}
          <div className="bg-[#1a1a1a] rounded-2xl p-6 text-white">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-1">
                <span className="text-[#f0b90b]">T.R.U.E.</span> Model
              </h2>
              <p className="text-sm text-[#888]">
                Tipping Rewards for User Engagement
              </p>
            </div>
            
            {isConnected && userRewards ? (
              <div className="space-y-4">
                {/* Your Stats */}
                <div className="bg-[#2a2a2a] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Gift className="w-5 h-5 text-[#f0b90b]" />
                    <span className="text-sm text-[#888]">Your Rewards</span>
                  </div>
                  <div className="text-3xl font-bold text-[#f0b90b] mb-2">
                    {formatBnbAmount(userRewards.totalEarnedWei, userRewards.totalEarned)} BNB
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-400">
                      ✓ {formatBnbAmount(userRewards.totalClaimedWei, userRewards.totalClaimed)} claimed
                    </span>
                    <span className="text-[#f0b90b]">
                      ○ {formatBnbAmount(userRewards.totalUnclaimedWei, userRewards.totalUnclaimed)} pending
                    </span>
                  </div>
                </div>
                
                {/* Activity */}
                <div className="bg-[#2a2a2a] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-[#f0b90b]" />
                    <span className="text-sm text-[#888]">Activity</span>
                  </div>
                  <div className="text-lg font-medium">
                    {userRewards.epochs?.length || 0} epochs participated
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-[#2a2a2a] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Trophy className="w-5 h-5 text-[#f0b90b]" />
                    <span className="font-medium">{Math.round(TIPPER_WEIGHT * 100)}% to Tippers</span>
                  </div>
                  <p className="text-sm text-[#888]">
                    Reward active engagement
                  </p>
                </div>
                
                <div className="bg-[#2a2a2a] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Award className="w-5 h-5 text-[#f0b90b]" />
                    <span className="font-medium">{Math.round(CREATOR_WEIGHT * 100)}% to Creators</span>
                  </div>
                  <p className="text-sm text-[#888]">
                    Passive rewards for content
                  </p>
                </div>
                
                <div className="bg-[#2a2a2a] rounded-xl p-5 border border-[#f0b90b]/30">
                  <div className="flex items-center gap-3 mb-2">
                    <Gift className="w-5 h-5 text-[#f0b90b]" />
                    <span className="font-medium">Connect Wallet</span>
                  </div>
                  <p className="text-sm text-[#888]">
                    Connect to view your rewards
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* My QR Code Section */}
        <div className="mt-6">
          <MyQRAccess variant="compact" showWhenNoQR={true} />
        </div>
        
      </div>
    </div>
  );
};
